import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AUDIT_CATEGORIES,
  canManageUsers,
  type AuditCategory,
  type AuditViewEntry,
} from '@jfh/intake-shared';
import { api, type Me } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { EmptyState, PageHeader } from '../components/Primitives.js';
import { useToast } from '../components/Toast.js';

/**
 * Audit log (admins): every recorded change, filterable, with before → after
 * detail and a CSV export of exactly what's filtered. Filters live in the URL.
 */

type Range = 'today' | '7d' | '30d' | 'all' | 'custom';
const RANGES: { key: Range; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom…' },
];

const CATEGORY_ICON: Record<AuditCategory, string> = {
  approvals: 'check',
  applications: 'user',
  iq: 'db',
  emails: 'mail',
  teams: 'teams',
  forms: 'forms',
  users: 'shield',
};

/** Today's date in the UK, YYYY-MM-DD. */
function ukToday(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(d);
}

const dayHeading = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'long',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const timeOfDay = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const dayKey = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' });

/** The API query for the current URL filters (no cursor). */
function queryFor(params: URLSearchParams): URLSearchParams {
  const q = new URLSearchParams();
  const range = (params.get('range') as Range | null) ?? '30d';
  if (range === 'today') q.set('from', ukToday());
  if (range === '7d') q.set('from', ukToday(-6));
  if (range === '30d') q.set('from', ukToday(-29));
  if (range === 'custom') {
    if (params.get('from')) q.set('from', params.get('from')!);
    if (params.get('to')) q.set('to', params.get('to')!);
  }
  for (const k of ['actor', 'cat', 'q', 'entity']) {
    const v = params.get(k);
    if (v) q.set(k, v);
  }
  return q;
}

export function AuditLog() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState<Me | null>(null);
  const [actors, setActors] = useState<{ actor: string; count: number }[]>([]);
  const [entries, setEntries] = useState<AuditViewEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState(params.get('q') ?? '');

  const range = (params.get('range') as Range | null) ?? '30d';
  const cats = useMemo(() => new Set((params.get('cat') ?? '').split(',').filter(Boolean)), [params]);
  const query = useMemo(() => queryFor(params), [params]);
  const queryKey = query.toString();
  const admin = me ? canManageUsers(me.role) : null;

  const update = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v) next.set(k, v);
        else next.delete(k);
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!admin) return;
    api.auditActors().then((r) => setActors(r.actors)).catch(() => undefined);
  }, [admin]);

  // Search box → URL, after a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('q') ?? '') !== search.trim()) update({ q: search.trim() || null });
    }, 300);
    return () => clearTimeout(t);
  }, [search, params, update]);

  useEffect(() => {
    if (!admin) return;
    let live = true;
    setEntries(null);
    setError(null);
    api
      .listAudit(new URLSearchParams(queryKey))
      .then((r) => {
        if (!live) return;
        setEntries(r.entries);
        setCursor(r.nextCursor);
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Failed to load the audit log'));
    return () => {
      live = false;
    };
  }, [admin, queryKey]);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const q = new URLSearchParams(queryKey);
      q.set('cursor', cursor);
      const r = await api.listAudit(q);
      setEntries((prev) => [...(prev ?? []), ...r.entries]);
      setCursor(r.nextCursor);
    } catch (e: unknown) {
      toast({ title: 'Could not load more', body: e instanceof Error ? e.message : undefined, icon: 'x' });
    } finally {
      setLoadingMore(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.exportAuditCsv(new URLSearchParams(queryKey));
      toast(
        r.truncatedAt
          ? { title: 'Exported the newest entries only', body: `Only the newest ${r.truncatedAt.toLocaleString('en-GB')} entries fit. Narrow the filters for the rest.`, icon: 'shield' }
          : { title: 'Audit log exported', body: 'The CSV is in your downloads.', icon: 'check' },
      );
    } catch (e: unknown) {
      toast({ title: 'Couldn’t export', body: e instanceof Error ? e.message : undefined, icon: 'x' });
    } finally {
      setExporting(false);
    }
  };

  const toggleCat = (c: AuditCategory) => {
    const next = new Set(cats);
    if (next.has(c)) next.delete(c);
    else next.add(c);
    update({ cat: [...next].join(',') || null });
  };

  if (admin === false) {
    return (
      <div style={{ padding: 28 }}>
        <EmptyState icon="shield" title="Admins only" body="You don't have permission to view the audit log." />
      </div>
    );
  }

  // Group by UK day, newest first (entries already arrive newest first).
  const days: { key: string; heading: string; items: AuditViewEntry[] }[] = [];
  for (const e of entries ?? []) {
    const d = new Date(e.at);
    const key = dayKey.format(d);
    const last = days[days.length - 1];
    if (last?.key === key) last.items.push(e);
    else days.push({ key, heading: dayHeading.format(d).replace(',', ''), items: [e] });
  }

  const entityLabel = params.get('entity') ? entries?.find((e) => e.subject?.id === params.get('entity'))?.subject?.label : null;

  return (
    <div style={{ padding: 26, maxWidth: 1100 }} className="fade-in">
      <PageHeader
        title="Audit log"
        subtitle="Every change, decision and message JFH Forms has recorded."
        actions={
          <button className="btn" onClick={() => void exportCsv()} disabled={exporting || !entries?.length}>
            <Icon name="upload" size={16} /> {exporting ? 'Exporting…' : 'Export what’s shown (CSV)'}
          </button>
        }
      />

      <div className="card" style={{ marginTop: 18, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-2)' }}>
            When
            <select
              className="select"
              value={range}
              onChange={(e) => update({ range: e.target.value === '30d' ? null : e.target.value })}
              style={{ minWidth: 150 }}
            >
              {RANGES.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          {range === 'custom' && (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-2)' }}>
                From
                <input className="input" type="date" value={params.get('from') ?? ''} onChange={(e) => update({ from: e.target.value || null })} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-2)' }}>
                To
                <input className="input" type="date" value={params.get('to') ?? ''} onChange={(e) => update({ to: e.target.value || null })} />
              </label>
            </>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-2)' }}>
            Who
            <select className="select" value={params.get('actor') ?? ''} onChange={(e) => update({ actor: e.target.value || null })} style={{ minWidth: 220 }}>
              <option value="">Everyone</option>
              {actors.map((a) => (
                <option key={a.actor} value={a.actor}>
                  {a.actor} ({a.count})
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-2)', flex: 1, minWidth: 200 }}>
            Search
            <input
              className="input"
              type="search"
              placeholder="Applicant, form, folder or person"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-2)', marginRight: 4 }}>What</span>
          {AUDIT_CATEGORIES.map((c) => {
            const on = cats.has(c.key);
            return (
              <button
                key={c.key}
                aria-pressed={on}
                onClick={() => toggleCat(c.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 11px',
                  borderRadius: 99,
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                  background: on ? 'var(--accent-soft)' : 'var(--bg-surface)',
                  color: on ? 'var(--accent-text)' : 'var(--text-2)',
                }}
              >
                <Icon name={CATEGORY_ICON[c.key]} size={13} />
                {c.label}
              </button>
            );
          })}
          {params.get('entity') && (
            <span className="badge neutral" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Only: {entityLabel ?? 'one item'}
              <button className="icon-btn" aria-label="Show everything" onClick={() => update({ entity: null })} style={{ width: 18, height: 18 }}>
                <Icon name="x" size={11} />
              </button>
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, padding: 16, color: 'var(--st-declined)' }}>
          {error}
        </div>
      )}
      {!error && entries === null && <div style={{ marginTop: 20, color: 'var(--text-3)' }}>Loading…</div>}
      {!error && entries?.length === 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <EmptyState icon="audit" title="Nothing recorded" body="No entries match these filters." />
        </div>
      )}

      {days.map((day) => (
        <section key={day.key} style={{ marginTop: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', margin: '0 0 8px 2px' }}>
            {day.heading}
          </div>
          <div className="card" style={{ padding: 0 }}>
            {day.items.map((e, i) => (
              <EntryRow key={e.id} entry={e} first={i === 0} />
            ))}
          </div>
        </section>
      ))}

      {cursor && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
          <button className="btn" onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

function EntryRow({ entry: e, first }: { entry: AuditViewEntry; first: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ padding: '12px 16px', borderTop: first ? 'none' : '1px solid var(--border)', display: 'flex', gap: 12 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 99,
          flex: 'none',
          background: 'var(--bg-sunken)',
          color: 'var(--text-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={CATEGORY_ICON[e.category]} size={14} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, overflowWrap: 'anywhere' }}>{e.summary}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <span>{timeOfDay.format(new Date(e.at))}</span>
          <span>·</span>
          <span>{e.actor}</span>
          {e.subject?.href && (
            <>
              <span>·</span>
              <Link to={e.subject.href} style={{ color: 'var(--accent-text)', fontWeight: 600 }}>
                Open {e.subject.label}
              </Link>
            </>
          )}
          {e.changes.length > 0 && (
            <>
              <span>·</span>
              <button
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontWeight: 600, fontSize: 12 }}
              >
                {open ? 'Hide' : 'Show'} {e.changes.length} change{e.changes.length === 1 ? '' : 's'}
              </button>
            </>
          )}
        </div>
        {e.note && (
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 6, padding: '7px 10px', background: 'var(--bg-sunken)', borderRadius: 7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {e.note}
          </div>
        )}
        {open && (
          <table style={{ marginTop: 8, width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-3)' }}>
                <th style={{ padding: '4px 8px 4px 0', fontWeight: 600, width: '28%' }}>Field</th>
                <th style={{ padding: '4px 8px', fontWeight: 600 }}>Before</th>
                <th style={{ padding: '4px 8px', fontWeight: 600 }}>After</th>
              </tr>
            </thead>
            <tbody>
              {e.changes.map((c, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)', verticalAlign: 'top' }}>
                  <td style={{ padding: '5px 8px 5px 0', fontWeight: 600 }}>{c.field}</td>
                  <td style={{ padding: '5px 8px', color: 'var(--text-2)', overflowWrap: 'anywhere', textDecoration: c.before ? 'line-through' : undefined }}>{c.before}</td>
                  <td style={{ padding: '5px 8px', overflowWrap: 'anywhere' }}>{c.after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
